import {
  AGREEMENT_TYPE,
  IAgreementTemplateResponse,
} from 'services/templates/agreement-template/types';
import { ICustomSectionTemplateResponse } from 'services/templates/section-template/types';
import {
  IContractTemplateTreeItemForm,
  IDistributionResponse,
  IDistributionType,
  TEMPLATE_TYPE,
} from 'services/templates/types';

export const distributionSectionQueryToForm = (
  distributionSection: IDistributionResponse,
  lowestId: number,
  type: IDistributionType
): IContractTemplateTreeItemForm => {
  const distributionChildren: IContractTemplateTreeItemForm[] =
    distributionSection.shares.map(
      ({ salesChannelId, percentage, salesChannelName }, key) => ({
        id: `share-${TEMPLATE_TYPE[type]}-${lowestId + key + 1}`,
        type: TEMPLATE_TYPE.agreement,
        content: {
          type: AGREEMENT_TYPE.shares,
          distributionTemplateShares: {
            percent: percentage,
            id: salesChannelId,
            name: salesChannelName ?? '',
          },
        },
        children: [],
      })
    );

  distributionChildren.unshift({
    id: `default-share-${TEMPLATE_TYPE[type]}`,
    type: TEMPLATE_TYPE.agreement,
    content: {
      type: AGREEMENT_TYPE.defaultShare,
      distributionTemplateShares: {
        percent: distributionSection.defaultSharePercentage,
        id: `default-share-agreement-${TEMPLATE_TYPE[type]}`,
        name: '',
      },
    },
    children: [],
    disabledSelection: true,
  });

  const distribution: IContractTemplateTreeItemForm = {
    id: `${lowestId}`,
    type: type,
    content: distributionSection.title,
    children: distributionChildren,
  };

  return distribution;
};

interface IConnectContractTreeItems {
  customSections?: ICustomSectionTemplateResponse[];
  digitalDistribution?: IDistributionResponse;
  physicalDistribution?: IDistributionResponse;
  merchDistribution?: IDistributionResponse;
  channelDistribution?: IDistributionResponse;
  agreements?: IAgreementTemplateResponse[];
}

export const connectTemplateTreeItems = (
  contractItems: IConnectContractTreeItems,
  lowestAvailableId?: string
): IContractTemplateTreeItemForm[] => {
  const {
    agreements,
    customSections,
    digitalDistribution,
    physicalDistribution,
    merchDistribution,
    channelDistribution,
  } = contractItems;

  const treeItems: IContractTemplateTreeItemForm[] = [];

  let lowestId = lowestAvailableId ? parseInt(lowestAvailableId) + 1 : 0;

  if (agreements?.length) {
    for (const agreement of agreements) {
      const { id, position, ...formAgreement } = agreement;
      if (formAgreement.type === AGREEMENT_TYPE.text) {
        treeItems[position] = {
          id: `${lowestId}`,
          type: TEMPLATE_TYPE.agreement,
          children: [],
          content: {
            type: AGREEMENT_TYPE.text,
            selectAgreementTemplate: {
              ...formAgreement,
              options: [formAgreement.defaultValue],
            },
            textAgreementTemplate: formAgreement,
          },
        };
      }
      if (formAgreement.type == AGREEMENT_TYPE.select) {
        const { options, ...textAgreement } = formAgreement;
        treeItems[position] = {
          id: `${lowestId}`,
          type: TEMPLATE_TYPE.agreement,
          children: [],
          content: {
            type: AGREEMENT_TYPE.select,
            textAgreementTemplate: textAgreement,
            selectAgreementTemplate: formAgreement,
          },
        };
      }
      lowestId += 1;
    }
  }

  if (digitalDistribution && !!digitalDistribution.title.length) {
    const digitalDistributionItem = distributionSectionQueryToForm(
      digitalDistribution,
      lowestId,
      TEMPLATE_TYPE.digitalDistribution
    );
    treeItems[digitalDistribution.position] = digitalDistributionItem;
    lowestId += digitalDistributionItem.children.length;
  }

  if (physicalDistribution && !!physicalDistribution.title.length) {
    const physicalDistributionItem = distributionSectionQueryToForm(
      physicalDistribution,
      lowestId,
      TEMPLATE_TYPE.physicalDistribution
    );
    treeItems[physicalDistribution.position] = physicalDistributionItem;
    lowestId += physicalDistributionItem.children.length;
  }
  if (merchDistribution && !!merchDistribution.title.length) {
    const merchDistributionItem = distributionSectionQueryToForm(
      merchDistribution,
      lowestId,
      TEMPLATE_TYPE.merchDistribution
    );
    treeItems[merchDistribution.position] = merchDistributionItem;
    lowestId += merchDistributionItem.children.length;
  }
  if (channelDistribution && !!channelDistribution.title.length) {
    const channelDistributionItem = distributionSectionQueryToForm(
      channelDistribution,
      lowestId,
      TEMPLATE_TYPE.channelDistribution
    );
    treeItems[channelDistribution.position] = channelDistributionItem;
    lowestId += channelDistributionItem.children.length;
  }

  if (customSections?.length) {
    for (const section of customSections) {
      const children = connectTemplateTreeItems(
        {
          agreements: section.agreements,
          customSections: section.customSections,
        },
        `${lowestId + 1}`
      );

      treeItems[section.position] = {
        id: `${lowestId}`,
        type: TEMPLATE_TYPE.section,
        content: section.title,
        children: children,
      };

      lowestId += children.length + 2;
    }
  }

  return treeItems;
};
